<template>
  <div class="remote-collection-field-renderer">
    <PButton
      style="width: 100%"
      :disabled="disabled"
      :loading="loading"
      @click="handleFetch"
    >
      {{ triggerLabel }}
    </PButton>

    <div
      v-if="rows.length && infoHint.length"
      class="remote-collection-field-renderer__info"
    >
      <p v-for="(linha, i) in infoHint" :key="i">{{ linha }}</p>
    </div>

    <table v-if="rows.length" class="remote-collection-field-renderer__table">
      <thead>
        <tr>
          <th class="remote-collection-field-renderer__check-col">#</th>
          <th v-for="col in columns" :key="col.Key">{{ col.Header }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rowIndex) in rows" :key="rowId(row, rowIndex)">
          <td class="remote-collection-field-renderer__check-col">
            <PCheckbox
              :model-value="isChecked(row)"
              @update:model-value="(v: boolean) => toggle(row, rowIndex, v)"
            />
          </td>
          <td v-for="col in columns" :key="col.Key">
            {{ cellText(row, col.Key) }}
          </td>
        </tr>
      </tbody>
    </table>

    <p
      v-else-if="fetched && emptyStateHint"
      class="remote-collection-field-renderer__empty"
    >
      {{ emptyStateHint }}
    </p>

    <span v-if="error" class="remote-collection-field-renderer__error">
      {{ error }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { PButton, PCheckbox } from "@f360-dev/f360-prisma";
import { ElMessageBox } from "element-plus";
import { computed, ref } from "vue";
import { IDefaultHttpFinancasResponse } from "@/core/@types/types";
import { TIntegracaoCampoModel } from "../../../@types/IntegracaoCampoModel";
import usePayloadResolver from "../../../composables/dynamicForm/handlers/usePayloadResolver/usePayloadResolver";

type TRemoteRow = Record<string, unknown>;

const props = defineProps<{
  campo: TIntegracaoCampoModel;
  modelValue?: TRemoteRow[] | null;
  error?: string;
  formValues?: Record<string, unknown>;
  actionContext?: Record<string, unknown>;
  executeFieldAction?: (
    metodo: string,
    endpoint: string,
    payload: Record<string, unknown>
  ) => Promise<IDefaultHttpFinancasResponse<unknown> | undefined>;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: TRemoteRow[]): void;
}>();

const loading = ref(false);
const fetched = ref(false);

const config = computed(() => props.campo.remoteCollection);

const triggerLabel = computed(
  () => config.value?.TriggerLabel ?? props.campo.label
);
const columns = computed(() => config.value?.Columns ?? []);
const infoHint = computed(() => config.value?.InfoHint ?? []);
const emptyStateHint = computed(() => config.value?.EmptyStateHint ?? null);

const idKey = computed(() => config.value?.IdKey ?? "Id");
const checkedKey = computed(() => config.value?.CheckedKey ?? "Checked");
const preserveBy = computed(
  () => config.value?.PreserveSelectionBy ?? idKey.value
);

const rows = computed<TRemoteRow[]>(() =>
  Array.isArray(props.modelValue) ? props.modelValue : []
);

const hasKeyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;

  return true;
};

const rowId = (row: TRemoteRow, rowIndex: number): string => {
  const value = row[idKey.value];

  return hasKeyValue(value) ? String(value) : `row-${rowIndex}`;
};

const selectionKey = (row: TRemoteRow, rowIndex: number): string => {
  const preserveValue = row[preserveBy.value];
  if (hasKeyValue(preserveValue)) return `key:${String(preserveValue)}`;

  const idValue = row[idKey.value];
  if (hasKeyValue(idValue)) return `key:${String(idValue)}`;

  return `index:${rowIndex}`;
};

const isChecked = (row: TRemoteRow): boolean => Boolean(row[checkedKey.value]);

const cellText = (row: TRemoteRow, key: string): string => {
  const value = row[key];

  return value === null || value === undefined ? "" : String(value);
};

const toggle = (row: TRemoteRow, rowIndex: number, value: boolean) => {
  const selectedId = row[idKey.value];
  const hasSelectedId = hasKeyValue(selectedId);
  const next = rows.value.map((item, itemIndex) => {
    const sameRow = hasSelectedId
      ? item[idKey.value] === selectedId
      : itemIndex === rowIndex;

    return sameRow ? { ...item, [checkedKey.value]: value } : item;
  });

  emit("update:modelValue", next);
};

const canFetch = computed(() => {
  const requiredFields = props.campo.action?.enableWhen ?? [];

  return requiredFields.every((field) => fieldHasValue(field));
});

const disabled = computed(() => loading.value || !canFetch.value);

const fieldHasValue = (fieldName: string): boolean => {
  const value = props.formValues?.[fieldName];

  if (typeof value === "string") return value.trim().length > 0;

  return value !== null && value !== undefined && value !== false;
};

const { buildPayload: buildActionPayload } = usePayloadResolver();

const buildPayload = (): Record<string, unknown> =>
  buildActionPayload(
    props.campo.action?.payload,
    props.formValues,
    props.actionContext
  );

const mergeSelection = (fetchedRows: TRemoteRow[]): TRemoteRow[] => {
  const previous = new Map(
    rows.value.map((item, index) => [
      selectionKey(item, index),
      item[checkedKey.value]
    ])
  );

  return fetchedRows.map((item, index) => {
    const previousValue = previous.get(selectionKey(item, index));

    return {
      ...item,
      [checkedKey.value]: Boolean(previousValue ?? item[checkedKey.value])
    };
  });
};

const extractRows = (
  response: IDefaultHttpFinancasResponse<unknown> | undefined
): TRemoteRow[] => {
  const result = (response as { Result?: unknown } | undefined)?.Result;

  return Array.isArray(result) ? (result as TRemoteRow[]) : [];
};

const handleFetch = async () => {
  const action = props.campo.action;
  if (!action || !props.campo.endpoint || !props.executeFieldAction) return;

  loading.value = true;
  try {
    const response = await props.executeFieldAction(
      action.method ?? "POST",
      props.campo.endpoint,
      buildPayload()
    );

    fetched.value = true;

    if (response?.Ok) {
      emit("update:modelValue", mergeSelection(extractRows(response)));

      return;
    }

    void ElMessageBox.alert(
      response?.Error ?? "Nao foi possivel obter os dados.",
      "",
      { type: "error", confirmButtonText: "OK" }
    );
  } catch (error) {
    void ElMessageBox.alert((error as Error).message, "", {
      type: "error",
      confirmButtonText: "OK"
    });
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped lang="scss" src="./RemoteCollectionFieldRenderer.scss"></style>
